import { API_BASE, REFRESH } from "./config.js";
import { getToken, getRefreshToken, getUserId, setTokens, clearSession } from "./session.js";

// Raised when the session is gone and could not be refreshed. The auth gate
// listens for the "te-auth-expired" event (dispatched below) to kick the user
// back to /login; callers can also catch this directly.
export class AuthError extends Error {
  constructor(message = "Session expired") {
    super(message);
    this.name = "AuthError";
  }
}

// A single in-flight refresh shared across concurrent 401s, so a burst of
// requests failing at once triggers exactly one refresh call, not N.
let refreshInFlight = null;

function doRefresh() {
  if (refreshInFlight) return refreshInFlight;

  const refreshToken = getRefreshToken();
  const userId = getUserId();
  if (!refreshToken || !userId) return Promise.reject(new AuthError("No refresh token"));

  const qs = new URLSearchParams({ userId, refreshToken }).toString();
  refreshInFlight = fetch(`${API_BASE}${REFRESH.path}?${qs}`, {
    method: REFRESH.method,
    headers: { accept: "application/json" },
  })
    .then(async (res) => {
      if (!res.ok) throw new AuthError("Refresh failed");
      const json = await res.json();
      const tokens = REFRESH.read(json);
      if (!tokens.ok || !tokens.authenticationToken) throw new AuthError("Refresh returned no token");
      setTokens(tokens);
      return tokens.authenticationToken;
    })
    .finally(() => { refreshInFlight = null; });

  return refreshInFlight;
}

// Core request helper. Attaches the bearer token, parses JSON, and on a 401
// transparently refreshes the token once and retries the original request.
// On any auth failure it clears the session and signals expiry.
// options.silentAuth: when true, a 401 that can't be refreshed will NOT clear
// the session or fire the global logout event — it just throws AuthError. Used
// for non-critical data calls (e.g. loading the store list) so a failed data
// fetch never logs the user out; the caller falls back to mock data instead.
export async function apiFetch(path, options = {}, _isRetry = false) {
  const token = getToken();
  const headers = {
    accept: "application/json, text/plain, */*",
    ...(options.body ? { "content-type": "application/json" } : {}),
    ...(token ? { authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };

  const res = await fetch(API_BASE + path, { ...options, headers });

  const handleAuthFailure = () => {
    if (options.silentAuth) throw new AuthError(); // don't log out on data calls
    clearSession();
    window.dispatchEvent(new Event("te-auth-expired"));
    throw new AuthError();
  };

  if (res.status === 401 && !_isRetry) {
    try {
      await doRefresh();
      return apiFetch(path, options, true); // retry once with the new token
    } catch {
      return handleAuthFailure();
    }
  }

  if (res.status === 401) {
    return handleAuthFailure();
  }

  const json = await res.json().catch(() => null);

  if (!res.ok || json?.status === "error") {
    // The API reports failures in either "message" or "error" depending on the
    // case (e.g. login lockout / wrong password use "error").
    const message = json?.message || json?.error || `Request failed (${res.status})`;
    throw new Error(message);
  }

  return json;
}
